// panelTimes — changing a case's scheduled start and end, safely.
//
// Pure. No React, no Firestore.
//
// ⚠️ EVERYTHING HERE IS THE *SCHEDULE*, NOT THE CASE.
// There is no actual start or end event anywhere in this system: `buildTimeline`
// reads only the scheduled "HH:MM" strings. So editing a time is the
// board-runner overwriting the schedule with a better estimate, and every label
// this feeds must say "Scheduled". Calling it "Ends" would claim knowledge the
// system does not have, on the number that drives the coverage flag and the
// boundary staffing math.

import { hmToMinutes, to12h } from "./timeline";

// A bad end time is worse than a rejected one, because `buildTimeline`
// SUBSTITUTES `start + 60 min` when the end does not parse or precedes the
// start (DEFAULT_CASE_MIN). The bar then looks entirely plausible and every
// derived number behind it is wrong. So this refuses rather than lets the model
// paper over it.
export function validateCaseTimes({ startTime, endTime, otherCases = [] } = {}) {
  const start = hmToMinutes(startTime);
  const end = hmToMinutes(endTime);

  if (startTime && start == null) return { ok: false, error: "That start time is not a time." };
  if (endTime && end == null) return { ok: false, error: "That end time is not a time." };

  // A null start turns the row into a full-width on-call bar — a running case
  // must not be able to become one by clearing a field.
  if (!startTime) return { ok: false, error: "A case needs a start time." };
  if (!endTime) return { ok: false, error: "A case needs an end time." };

  if (end <= start) {
    return { ok: false, error: "The end must be after the start." };
  }

  // Two cases in one room at the same moment is not a schedule, it is a
  // mistake — and the timeline would draw them stacked, which reads as one
  // long case.
  for (const other of (Array.isArray(otherCases) ? otherCases : [])) {
    if (!other) continue;
    const oStart = Number.isFinite(other.startMin) ? other.startMin : hmToMinutes(other.startTime);
    const oEnd = Number.isFinite(other.endMin) ? other.endMin : hmToMinutes(other.endTime);
    if (oStart == null || oEnd == null) continue;
    if (start < oEnd && end > oStart) {
      return {
        ok: false,
        error: `That overlaps the ${to12h(oStart)}–${to12h(oEnd)} case in the same room.`,
      };
    }
  }

  return { ok: true, error: null };
}

function durationLabel(min) {
  if (!Number.isFinite(min) || min <= 0) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} m`;
  return m ? `${h} h ${m} m` : `${h} h`;
}

// The change stated in WORDS, because the lane ghost states it in space and
// neither alone is enough at a glance.
export function timeChangeSummary({ fromStart, fromEnd, toStart, toEnd } = {}) {
  const a0 = hmToMinutes(fromStart);
  const a1 = hmToMinutes(fromEnd);
  const b0 = hmToMinutes(toStart);
  const b1 = hmToMinutes(toEnd);
  if (b0 == null || b1 == null || b1 <= b0) return "";

  const was = a0 != null && a1 != null && a1 > a0 ? durationLabel(a1 - a0) : "";
  const now = durationLabel(b1 - b0);
  const head = was && was !== now ? `${was} → ${now}` : now;
  return `${head} · scheduled to end ${to12h(b1)}`;
}

// "The person in this room now leaves before the case is scheduled to finish."
//
// This is a WARNING, never an error, and it must NOT block the save. It is a
// true state that the board exists to display — the whole point of the COVER TO
// column is to show it. Refusing the edit would mean the board could not
// represent a room that is genuinely about to be uncovered.
export function coverageConsequence({ endTime, departMin } = {}) {
  const end = hmToMinutes(endTime);
  if (end == null || !Number.isFinite(departMin)) return null;
  if (departMin >= end) return null;
  return {
    minutesShort: end - departMin,
    text: `Cover ends ${to12h(departMin)}, ${end - departMin} min short of the new end.`,
  };
}

// Step a time field by whole minutes, staying inside the day.
export function stepTime(hm, deltaMin) {
  const at = hmToMinutes(hm);
  if (at == null || !Number.isFinite(deltaMin)) return hm;
  const next = Math.max(0, Math.min(23 * 60 + 55, at + deltaMin));
  const h = Math.floor(next / 60);
  const m = next % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
